// Admin Dashboard API
// Powered by Supabase
// NOTE: These functions require admin role in agents table

import { supabase } from '../lib/supabase';
import { isAdmin } from './auth';

// Check if current user is admin
async function requireAdmin() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('Not authenticated');
  }

  const adminStatus = await isAdmin(user.id);
  
  if (!adminStatus) {
    throw new Error('Admin access required');
  }

  return user;
}

// =====================================================
// DASHBOARD STATISTICS
// =====================================================

export async function getDashboardStats() {
  try {
    await requireAdmin();

    // Get counts for all tables
    const [
      { count: applicationsCount },
      { count: contactsCount },
      { count: ordersCount },
      { count: agentsCount },
    ] = await Promise.all([
      supabase.from('applications').select('*', { count: 'exact', head: true }),
      supabase.from('contacts').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('*', { count: 'exact', head: true }),
      supabase.from('agents').select('*', { count: 'exact', head: true }),
    ]);

    // Get pending items
    const [
      { count: pendingApplications },
      { count: newContacts },
      { count: pendingOrders },
    ] = await Promise.all([
      supabase.from('applications').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('contacts').select('*', { count: 'exact', head: true }).eq('status', 'new'),
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
    ]);

    // Get total revenue from orders
    const { data: orders } = await supabase
      .from('orders')
      .select('total_amount');

    const totalRevenue = orders?.reduce((sum, order) => sum + Number(order.total_amount), 0) || 0;

    return {
      success: true,
      data: {
        applications: {
          total: applicationsCount || 0,
          pending: pendingApplications || 0,
        },
        contacts: {
          total: contactsCount || 0,
          new: newContacts || 0,
        },
        orders: {
          total: ordersCount || 0,
          pending: pendingOrders || 0,
          revenue: totalRevenue,
        },
        agents: {
          total: agentsCount || 0,
        },
      },
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return { success: false, error: 'Failed to fetch dashboard statistics' };
  }
}

// =====================================================
// RECENT ACTIVITY
// =====================================================

export async function getRecentActivity(limit: number = 10) {
  try {
    await requireAdmin();

    // Get recent items from all tables
    const [
      { data: recentApplications },
      { data: recentContacts },
      { data: recentOrders },
    ] = await Promise.all([
      supabase
        .from('applications')
        .select('id, created_at, full_name, email, status')
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('contacts')
        .select('id, created_at, name, email, status')
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('orders')
        .select('id, created_at, customer_name, product_name, status, total_amount')
        .order('created_at', { ascending: false })
        .limit(limit),
    ]);

    // Combine and sort by date
    const activity = [
      ...(recentApplications || []).map((item) => ({ ...item, type: 'application' })),
      ...(recentContacts || []).map((item) => ({ ...item, type: 'contact' })),
      ...(recentOrders || []).map((item) => ({ ...item, type: 'order' })),
    ]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);

    return { success: true, data: activity };
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    return { success: false, error: 'Failed to fetch recent activity' };
  }
}

// =====================================================
// ANALYTICS
// =====================================================

export async function getAnalytics(days: number = 30) {
  try {
    await requireAdmin();

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    // Fetch from 60 days earlier to capture orders created before period but paid during it
    const fetchFrom = new Date(startDate);
    fetchFrom.setDate(fetchFrom.getDate() - 60);

    // Get data for the specified period
    const [
      { data: applications },
      { data: contacts },
      { data: orders },
    ] = await Promise.all([
      supabase
        .from('applications')
        .select('created_at, status')
        .gte('created_at', startDate.toISOString()),
      supabase
        .from('contacts')
        .select('created_at, status')
        .gte('created_at', startDate.toISOString()),
      supabase
        .from('orders')
        .select('created_at, paid_at, status, total_amount')
        .gte('created_at', fetchFrom.toISOString()),
    ]);

    // Group by date
    const dateMap = new Map();

    applications?.forEach((item) => {
      const date = item.created_at.split('T')[0];
      if (!dateMap.has(date)) {
        dateMap.set(date, { date, applications: 0, contacts: 0, orders: 0, revenue: 0 });
      }
      dateMap.get(date).applications++;
    });

    contacts?.forEach((item) => {
      const date = item.created_at.split('T')[0];
      if (!dateMap.has(date)) {
        dateMap.set(date, { date, applications: 0, contacts: 0, orders: 0, revenue: 0 });
      }
      dateMap.get(date).contacts++;
    });

    const startStr = startDate.toISOString().split('T')[0];
    orders?.forEach((item) => {
      // Use paid_at when available (matches fulfillment "yesterday's orders"); otherwise created_at
      const orderDate = item.paid_at || item.created_at;
      const date = orderDate.split('T')[0];
      if (date < startStr) return; // Only include orders in the requested period
      if (!dateMap.has(date)) {
        dateMap.set(date, { date, applications: 0, contacts: 0, orders: 0, revenue: 0 });
      }
      dateMap.get(date).orders++;
      dateMap.get(date).revenue += Number(item.total_amount);
    });

    const analytics = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    return { success: true, data: analytics };
  } catch (error) {
    console.error('Error fetching analytics:', error);
    return { success: false, error: 'Failed to fetch analytics' };
  }
}

// =====================================================
// AGENTS MANAGEMENT
// =====================================================

export async function createAgentAccount(data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
}) {
  try {
    await requireAdmin();

    const { data: result, error } = await supabase.functions.invoke('create-agent', {
      body: data,
    });

    if (error) {
      console.error('Edge function invocation error:', error);
      return { success: false, error: error.message || 'Failed to invoke create-agent function' };
    }

    if (!result?.success) {
      return { success: false, error: result?.error || 'Failed to create agent account' };
    }

    return {
      success: true,
      emailSent: result.emailSent,
      agentEmail: result.agentEmail,
      message: result.message,
    };
  } catch (error: any) {
    console.error('Error creating agent account:', error);
    return { success: false, error: error?.message || 'Failed to create agent account' };
  }
}

export async function getAllAgents() {
  try {
    await requireAdmin();

    const { data, error } = await supabase
      .from('agents')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching agents:', error);
    return { success: false, error: 'Failed to fetch agents' };
  }
}

export async function updateAgentStatus(agentId: string, status: string) {
  try {
    await requireAdmin();

    const { error } = await supabase
      .from('agents')
      .update({ status })
      .eq('id', agentId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating agent status:', error);
    return { success: false, error: 'Failed to update agent status' };
  }
}

export async function updateAgentRole(agentId: string, role: string) {
  try {
    await requireAdmin();

    const { error } = await supabase
      .from('agents')
      .update({ role })
      .eq('id', agentId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating agent role:', error);
    return { success: false, error: 'Failed to update agent role' };
  }
}

// =====================================================
// BULK OPERATIONS
// =====================================================

export async function bulkUpdateStatus(table: string, ids: string[], status: string) {
  try {
    await requireAdmin();

    const { error } = await supabase
      .from(table)
      .update({ status })
      .in('id', ids);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error bulk updating status:', error);
    return { success: false, error: 'Failed to bulk update status' };
  }
}

export async function bulkDelete(table: string, ids: string[]) {
  try {
    await requireAdmin();

    const { error } = await supabase
      .from(table)
      .delete()
      .in('id', ids);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error bulk deleting:', error);
    return { success: false, error: 'Failed to bulk delete' };
  }
}

// =====================================================
// EXPORT DATA
// =====================================================

export async function exportData(table: string, format: 'csv' | 'json' = 'csv') {
  try {
    await requireAdmin();

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    if (format === 'json') {
      return { success: true, data, format: 'json' };
    }

    // Convert to CSV
    if (!data || data.length === 0) {
      return { success: false, error: 'No data to export' };
    }

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((header) => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      ),
    ];

    return { success: true, data: csvRows.join('\n'), format: 'csv' };
  } catch (error) {
    console.error('Error exporting data:', error);
    return { success: false, error: 'Failed to export data' };
  }
}
